import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import MaraSportsLogo from '../components/MaraSportsLogo';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Trophy, Shield, Lock, Mail, ArrowLeft } from 'lucide-react';

const AdminLoginPage = () => {
    const navigate = useNavigate();
    const { login, logout } = useAuth();
    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState({
        email: '',
        password: ''
    });

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            const userData = await login(formData.email, formData.password);
            // List of allowed admin panel roles
            const allowedRoles = [
                'master_admin', 'ministry_admin', 'education_admin', 'nock_admin', 'federation_admin', 
                'club_admin', 'school_admin',
                'admin', 'tournament_chairman', 'tournament_director', 
                'official_referee', 'account_manager', 'admin_team'
            ];
            
            if (userData && allowedRoles.includes(userData.role)) {
                // Customize welcome message based on role
                const roleNames = {
                    'master_admin': 'Master Admin',
                    'ministry_admin': 'Ministry of Sports',
                    'education_admin': 'Ministry of Education',
                    'nock_admin': 'NOCK Admin',
                    'federation_admin': 'Federation Admin',
                    'club_admin': 'Club Admin',
                    'school_admin': 'School Admin',
                    'admin': 'Admin',
                    'tournament_chairman': 'Tournament Chairman',
                    'tournament_director': 'Tournament Director',
                    'official_referee': 'Official',
                    'account_manager': 'Account Manager',
                    'admin_team': 'Team Admin'
                };
                toast.success(`Welcome, ${roleNames[userData.role] || 'User'}!`);
                navigate('/admin');
            } else {
                toast.error('Admin access required. You do not have admin privileges.');
                logout();
            }
        } catch (error) {
            console.error('Login error:', error);
            toast.error(error.response?.data?.detail || 'Login failed. Please check your credentials.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-[#0a1628] flex items-center justify-center p-6">
            <div className="fixed inset-0 grid-lines pointer-events-none" style={{ zIndex: 0 }} />
            
            <div className="relative z-10 w-full max-w-md">
                {/* Back to Home */}
                <Link to="/" className="flex items-center gap-2 text-muted-foreground hover:text-white mb-8 transition-colors">
                    <ArrowLeft className="w-4 h-4" />
                    <span className="text-sm">Back to Home</span>
                </Link>

                {/* Admin Logo */}
                <div className="flex items-center justify-center gap-3 mb-8">
                    <MaraSportsLogo size={48} />
                    <div>
                        <h1 className="font-heading font-bold text-2xl tracking-tight uppercase text-white">
                            NextGen<span className="text-primary"> Tournaments</span>
                        </h1>
                        <div className="flex items-center gap-2">
                            <Shield className="w-3 h-3 text-primary" />
                            <span className="text-xs tracking-widest uppercase text-primary">Admin Portal</span>
                        </div>
                    </div>
                </div>

                {/* Login Card */}
                <div className="bg-[#0d2847] border border-white/10 p-8">
                    <div className="text-center mb-8">
                        <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                            <Lock className="w-8 h-8 text-primary" />
                        </div>
                        <h2 className="font-heading font-bold text-xl uppercase tracking-tight text-white">
                            Admin Login
                        </h2>
                        <p className="text-sm text-muted-foreground mt-2">
                            Enter your admin credentials to access the dashboard
                        </p>
                    </div>

                    <form onSubmit={handleSubmit} className="space-y-6">
                        <div className="space-y-2">
                            <Label className="text-xs tracking-widest uppercase text-muted-foreground">
                                Email Address
                            </Label>
                            <div className="relative">
                                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                                <Input
                                    type="email"
                                    placeholder="admin@nextgentournaments.com"
                                    value={formData.email}
                                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                    className="bg-[#0a1628] border-white/10 h-12 pl-10"
                                    required
                                    data-testid="admin-email-input"
                                />
                            </div>
                        </div>

                        <div className="space-y-2">
                            <Label className="text-xs tracking-widest uppercase text-muted-foreground">
                                Password
                            </Label>
                            <div className="relative">
                                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                                <Input
                                    type="password"
                                    placeholder="••••••••"
                                    value={formData.password}
                                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                                    className="bg-[#0a1628] border-white/10 h-12 pl-10"
                                    required
                                    data-testid="admin-password-input"
                                />
                            </div>
                        </div>

                        <Button
                            type="submit"
                            disabled={loading}
                            className="w-full bg-primary hover:bg-cyan-400 text-[#0a1628] h-12 uppercase tracking-widest text-sm font-bold"
                            data-testid="admin-login-btn"
                        >
                            {loading ? (
                                <div className="w-5 h-5 border-2 border-[#0a1628] border-t-transparent rounded-full animate-spin" />
                            ) : (
                                <>
                                    <Shield className="w-4 h-4 mr-2" />
                                    Access Admin Panel
                                </>
                            )}
                        </Button>
                    </form>

                    <div className="mt-6 pt-6 border-t border-white/10 text-center space-y-3">
                        <p className="text-xs text-muted-foreground">
                            Need an admin account?{' '}
                            <Link to="/admin/register" className="text-primary hover:underline">
                                Register as Admin
                            </Link>
                        </p>
                        <p className="text-xs text-muted-foreground">
                            Not an admin?{' '}
                            <Link to="/auth" className="text-primary hover:underline">
                                Regular Sign In
                            </Link>
                        </p>
                    </div>
                </div>

                {/* Security Notice */}
                <div className="mt-6 p-4 bg-warning/10 border border-warning/20">
                    <div className="flex items-start gap-3">
                        <Shield className="w-5 h-5 text-warning flex-shrink-0 mt-0.5" />
                        <div>
                            <p className="text-sm text-warning font-medium">Secure Admin Access</p>
                            <p className="text-xs text-muted-foreground mt-1">
                                This portal is for authorized administrators only. All access attempts are logged.
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AdminLoginPage;
